import { Logout, Menu, VerifiedUser } from "@mui/icons-material";
import { Badge, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Modal, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { acceptOrder, getNewOrders } from "../../State/Order/Action";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { updateProfile } from "../../State/Employee/Action";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const Header = ({ onToggle, onLogout, user, loading }) => {
  const dispatch = useDispatch();
  const { employeeOrders } = useSelector((store) => store);
  const [openModal, setOpenModal] = useState(false);
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [openProfile, setOpenProfile] = useState(false);
  const [editData, setEditData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    password: ""
  });

  useEffect(() => {
    if (user) {
      setEditData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        password: ""
      });
    }
    setShowPassword(false)
  }, [user, openProfile]);

  const handleChange = (e) => {
    // Allow only digits in phone
    if (e.target.name === "phone" && !/^\d*$/.test(e.target.value)) return;
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (!editData.name.trim()) {
      toast.error("Name is required!!");
      return;
    }
    if (!editData.phone.trim()) {
      toast.error("Phone Number is required!!");
      return;
    }
    if (editData.password.trim()) {
      if (editData.password.length < 6 || editData.password.length > 15) {
        toast.error("Password should be between 6 to 15 characters");
        return;
      }
    }
    dispatch(updateProfile(user?.id, editData, setOpenProfile))
  };


  useEffect(() => {
    dispatch(getNewOrders());
  }, [dispatch]);

  const handleAccept = (orderId) => {
    if (orderId) {
      dispatch(acceptOrder(orderId));
    }
  };

  const newOrdersCount = useSelector(
    (state) =>
      state.employeeOrders?.newOrders?.response?.filter(o => o?.orderStatus !== "CANCELLED")?.length || 0
  );

  const getStatusChip = (status) => {
    const colorMap = {
      "PENDING": "warning",
      "PLACED": "dark",
      "SHIPPED": "info",
      "DELIVERED": "success",
      "CANCELLED": "error"
    };
    return <Chip label={status} color={colorMap[status] || "default"} variant="contained" sx={{ fontSize: "0.7rem", height: "24px", borderRadius: "12px", fontWeight: "bold" }} />;
  };


  return (
    <>
      <header className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
        <button className=" hover:text-red-500 hover:bg-white" onClick={onToggle}>
          <Menu />
        </button>
        <div className="flex items-center gap-4 ml-auto">
          <div className="flex justify-end items-center me-3">
            <Badge
              badgeContent={newOrdersCount}
              color="error"
              invisible={newOrdersCount === 0}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  px: 2,
                  py: 1,
                  // optional: ensure enough padding for the badge
                  position: "relative",
                }}
                onClick={() => setOpenModal(true)}
              >
                New Orders
              </Button>
            </Badge>
          </div>
          <div className="relative group">
            {/* Avatar */}
            <div
              className="h-8 w-8 rounded-full bg-red-500 text-white flex items-center justify-center text-md font-bold uppercase cursor-pointer"
            >
              {user?.name?.charAt(0) || "E"}
            </div>

            {/* Dropdown */}
            <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="px-4 py-3 text-sm">
                <div className="font-semibold">{user?.name || "Employee"}</div>
                <div className="text-gray-500 text-xs">{user?.email || "Employee@gmail.com"}</div>
              </div>
              <hr />
              <button
                onClick={() => setOpenProfile(true)}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 transition">
                <VerifiedUser className="h-4 w-4" /> Profile
              </button>
              <button
                onClick={onLogout}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 transition text-red-500"
              >
                <Logout className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Profile Modal */}
      {openProfile && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 px-4">
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            {/* Close Icon */}
            <button
              onClick={() => setOpenProfile(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 hover:bg-white text-xl font-bold"
            >
              ✕
            </button>

            <h2 className="text-xl font-bold mb-4 text-center">Profile Details</h2>

            {/* Name */}
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={editData.name}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 mb-3 focus:ring focus:ring-red-200 outline-none"
            />

            {/* Email (read-only) */}
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={editData.email}
              readOnly
              className="w-full border rounded-md px-3 py-2 mb-3 bg-gray-100 cursor-not-allowed"
            />

            {/* Phone */}
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="text"
              name="phone"
              value={editData.phone}
              onChange={handleChange}
              maxLength={10}
              className="w-full border rounded-md px-3 py-2 mb-3 focus:ring focus:ring-red-200 outline-none"
            />

            {/* Password */}
            <div className="relative w-full">
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={editData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                className="w-full border rounded-md px-3 py-2 pr-10 mb-6 focus:ring focus:ring-red-200 outline-none"
              />
              <button
                type="button"
                className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {/* Centered Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleSave}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg w-full sm:w-auto"
              >
                {loading ? "Updating...." : "Save Changes"}
              </button>
              <button
                onClick={() => { setOpenProfile(false); onLogout(); }}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg w-full sm:w-auto"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
      {/* New Orders Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-md shadow-lg w-[90vw] h-[85vh] max-w-5xl overflow-auto">
          {/* Modal Header */}
          <div className="flex justify-between items-center  pb-2">
            <h2 className="text-xl font-semibold text-uppercase">NEW ORDERS</h2>
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon />
            </IconButton>
          </div>

          <Paper className="mt-2">
            <DataGrid
              rows={
                employeeOrders.newOrders?.response?.filter(order => order?.orderStatus !== "CANCELLED")
                  ?.map((order, index) => ({
                    id: `${order.id}-${index}`, // Ensures uniqueness and avoids undefined/null
                    orderId: order.id,
                    orderDate: order?.orderDate || "----",
                    uniqueOrderId: order.orderId,
                    name: order.user?.name || order.shippingAddress?.name || "----",
                    phone: order.user?.phone || order.shippingAddress?.mobile || "----",
                    address: `${order.shippingAddress?.city}, ${order.shippingAddress?.state}, ${order.shippingAddress?.zipcode}, ${order.shippingAddress?.mobile}` || "----",
                    vendorName: order?.orderItems?.map((item) => item?.product?.vendor?.name).join(", ") || "----",
                    vendorPhone: order?.orderItems?.map((item) => item?.product?.vendor?.phone).join(", ") || "----",
                    deliveryDate: order?.deliveryDate || "----",
                    dispatchDate: order?.dispatchDate,
                    totalItem: order.totalItem || 0,
                    totalPrice: order.orderTotalPrice?.toFixed(2) || 0,
                    orderStatus: order?.orderStatus,
                    fullOrder: order,
                    customSizes: order?.customSizes,
                  })) || []
              }

              columns={[
                {
                  field: "serialNo",
                  headerName: "S.No.",
                  width: 60,
                  sortable: false,
                  renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
                },
                { field: "uniqueOrderId", headerName: "Order ID", flex: 1.2 },
                { field: "orderDate", headerName: "Order Date", flex: 1.2, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:MM") : "----", },
                { field: "address", headerName: "Shipping Address", flex: 2 },
                { field: "vendorName", headerName: "Vendor Name", flex: 1.5 },
                { field: "vendorPhone", headerName: "Vendor Phone", flex: 1.5 },
                { field: "dispatchDate", headerName: "Dispatch Date", flex: 1.2, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----", },
                { field: "deliveryDate", headerName: "Delivery Date", flex: 1.2, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----", },
                { field: "totalItem", headerName: "Total Item", flex: 1 },
                { field: "totalPrice", headerName: "Total Price", flex: 1 },
                {
                  field: "orderStatus",
                  headerName: "Status",
                  flex: 1.5,
                  renderCell: (params) => getStatusChip(params.value === "PLACED" ? "ORDER ACCEPT" : params.value)
                },
                {
                  field: "action",
                  headerName: "Action",
                  flex: 1.5,
                  renderCell: (params) => {
                    const hasCustomSizes = params?.row?.customSizes?.length > 0;
                    const isPending = params?.row?.orderStatus === "PENDING";

                    return (
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        disabled={hasCustomSizes || isPending} // ❌ disable only if both true
                        onClick={() => {
                          setSelectedOrder(params.row); // set full order row
                          setAcceptConfirmOpen(true);
                        }}
                      >
                        Pick
                      </Button>
                    );
                  },
                }
              ]}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
              getRowId={(row) => row.id}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={employeeOrders.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />

          </Paper>

        </Box>
      </Modal>

      {/* Accept order dialog box */}
      <Dialog
        open={acceptConfirmOpen}
        onClose={() => setAcceptConfirmOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 10px 30px rgba(0,0,0,0.2)",
          },
        }}
      >
        {/* Title with styling */}
        <DialogTitle
          sx={{
            fontSize: "1.4rem",
            fontWeight: 600,
            textAlign: "center",
            px: 3,
            py: 2,
            backgroundColor: "#f3f4f6",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          Confirm Order Acceptance
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent
          dividers
          sx={{ px: 3, py: 2, backgroundColor: "#f9fafb" }}
        >
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* User Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  🙍‍♂️ User Details
                </Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedOrder.name}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {selectedOrder.phone}</Typography>
                <Typography variant="body2"><strong>Address:</strong> {selectedOrder.address}</Typography>
              </Grid>

              {/* Vendor Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  🏪 Vendor Details
                </Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedOrder.vendorName}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {selectedOrder.vendorPhone}</Typography>
              </Grid>

              {/* Order Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  📦 Order Details
                </Typography>
                <Typography variant="body2"><strong>Order ID:</strong> {selectedOrder.uniqueOrderId}</Typography>
                <Typography variant="body2"><strong>Order Date:</strong> {dayjs(selectedOrder.orderDate).format("DD MMM YYYY HH:MM")}</Typography>
                <Typography variant="body2"><strong>Dispatch Date:</strong> {dayjs(selectedOrder.dispatchDate).format("DD MMM YYYY")}</Typography>
                <Typography variant="body2"><strong>Delivery Date:</strong> {dayjs(selectedOrder.deliveryDate).format("DD MMM YYYY")}</Typography>
                <Typography variant="body2"><strong>Total Items:</strong> {selectedOrder.totalItem}</Typography>
                <Typography variant="body2"><strong>Total Price:</strong> ₹{selectedOrder.totalPrice}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#f3f4f6",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={() => setAcceptConfirmOpen(false)}
            variant="outlined"
            color="error"
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#fee2e2",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              handleAccept(selectedOrder?.orderId);
              setAcceptConfirmOpen(false);
            }}
            sx={{
              background: "linear-gradient(90deg, #22c55e, #16a34a)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1,
              boxShadow: "0px 4px 12px rgba(34,197,94,0.2)",
              "&:hover": {
                background: "linear-gradient(90deg, #16a34a, #22c55e)",
                transform: "scale(1.02)",
              },
            }}
          >
            ✅ Accept Order
          </Button>
        </DialogActions>
      </Dialog>

    </>
  )
};

export default Header;